import {buildResponsiveImageData} from 'Shared/buildResponsiveImageData';

import type {HeroSize} from './HeroBlock';
import type {Media as MediaType} from 'types/payload-types';

/** Requested image width (px) for the `small` hero size. */
const SMALL_IMAGE_WIDTH = 600;
/** Requested image width (px) for the `medium`/`large` hero sizes. */
const LARGE_IMAGE_WIDTH = 1200;

/**
 * Extracts the best available image URL from a Media document.
 *
 * @param mediaDoc The Media document or string ID.
 * @returns The image URL or null.
 */
const getImageUrl = (mediaDoc: MediaType | string | null | undefined): string | null => {
    if (!mediaDoc || typeof mediaDoc === 'string') return null;

    return mediaDoc.sizes?.hero?.url ?? mediaDoc.sizes?.card?.url ?? mediaDoc.url ?? null;
};

/**
 * Custom hook for the `HeroBlock` component. Hosts the single "hook" call — required by the
 * `@nfq/component-single-hook` lint rule, which limits the component body itself to one hook
 * call plus guard clauses/returns — and derives the resolved image URL, the populated Media
 * document, and the responsive image data used for the `ResponsiveImage`.
 *
 * @param backgroundImage The hero background image (populated Media document or ID).
 * @param size            The hero display size, used to pick the requested image width.
 * @returns The resolved image URL, the populated Media document (if any), and the responsive image data.
 */
export const useHeroBlock = (backgroundImage: MediaType | string | null | undefined, size: HeroSize) => {
    const imageUrl = getImageUrl(backgroundImage);
    const media = typeof backgroundImage === 'object' ? backgroundImage : null;
    const imgData = buildResponsiveImageData(media, size === 'small' ? SMALL_IMAGE_WIDTH : LARGE_IMAGE_WIDTH);

    return {
        imageUrl,
        imgData,
        media
    };
};